import Button from 'components/UI/Buttons/Button';
import LoadingSpinner from 'components/UI/LoadingIndicators/LoadingSpinner';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import produce from 'immer';
import {
    get, set, findIndex,
} from 'lodash';

import { webInvoiceUrl, webItineraryUrl, webQuoteUrl } from 'utils/url';
import { sortReservationsByCheckin } from 'utils/sorting-methods';
import ellipsis from 'utils/ellipsis';
import ReservationComponents from 'components/BusinessLogic/SearchReservationByBookingNumber/ReservationComponents/Loadable';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { connect } from 'react-redux';
import { loadReservations } from 'store/dispatchers/reservations.dispatchers';

const reservationIncludes = [
    'request.branding',
    'reservationFlights.numbers',
    'reservationFlights.reservationFlightMembers',
    'reservationFlights.reservationFlightLegs.airline',
    'reservationFlights.reservationFlightLegs.departureFromAirport',
    'reservationFlights.reservationFlightLegs.departureToAirport',
    'reservationHotels.hotel.destination.parent',
    'reservationHotels.hotel.destination.destinationDocuments',
    'reservationHotels.reservationHotelDates',
    'reservationHotels.members',
    'reservationHotels.numbers',
    'reservationHotels.reservationHotelRooms.reservationHotelRoomMembers',
    'reservationHotels.reservationHotelRooms.hotelRoom',
    'reservationInsurances.insurance',
    'reservationInsurances.numbers',
    'reservationServices.numbers',
    'reservationServices.reservationServiceMembers',
    'reservationServices.vendorService.vendor',
];

function SearchReservation({ onClose, searchMemberReservations })
{
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [reservations, setReservations] = useState([]);
    const [searched, setSearched] = useState(false);

    const loadData = () =>
    {
        if (!searchTerm)
        {
            setError('Search field cannot be empty.');
            return;
        }

        if (isLoading) return;

        setSearched(true);
        setError('');
        setIsLoading(true);
        setReservations([]);

        searchMemberReservations({ criteria: searchTerm }, reservationIncludes).then((results) =>
        {
            if (results.data.length === 0)
            {
                setError('Sorry, no reservations found by the criteria provided.');
                return;
            }

            setReservations(sortReservationsByCheckin(results.data, 'DESC'));
        }).catch((e) =>
        {
            setError(e.mergeMessages);
        }).finally(() =>
        {
            setIsLoading(false);
        });
    };

    const toggleViewReservation = (reservationId) =>
    {
        const index = findIndex(reservations, { id: reservationId });
        setReservations(produce(reservations, (draft) =>
        {
            set(draft, `${index}.showFull`, !get(draft, `${index}.showFull`));
        }));
    };

    return (
        <div className="tw-container">
            <div className="dark-layer" />
            <div className="popover-content">
                <div className="popover-close">
                    <i
                        onClick={() =>
                        {
                            onClose();
                        }}
                        className="material-icons remove-glow"
                        role="button"
                        tabIndex="0"
                        onKeyUp={() =>
                        {
                            onClose();
                        }}
                    >close</i>
                </div>

                <section className="section-style">
                    <div className="tw-container">

                        {/* Search Input */}
                        <h2 className="tw-uppercase tw-my-10 default-letter-spacing tw-font-bold tw-text-center tw-text-white">Search</h2>

                        <div className="tw-text-center tw-mx-3 tw-mb-8">
                            <div className="search-container">
                                <input
                                    readOnly={isLoading}
                                    onKeyUp={(e) => e.key === 'Enter' && loadData()}
                                    className="search-style tw-rounded-full dark:tw-bg-neutral-600"
                                    value={searchTerm}
                                    onChange={(v) => setSearchTerm(v.target.value)}
                                    placeholder="Quote ID, Booking Number, Traveler Name"
                                />
                                <Button
                                    onClick={() => !isLoading && loadData()}
                                    content={isLoading
                                        ? <LoadingSpinner loading className="tw-mx-3 tw-text-gray-500" />
                                        : <FontAwesomeIcon icon={faSearch} className="tw-mx-3 tw-text-gray-500  dark:tw-text-gray-300" />}
                                    className="search-btn-style dark:tw-bg-neutral-600 dark:tw-text-gray-300"
                                />
                            </div>
                        </div>

                        {/* Errors */}
                        {error !== ''
                            && (
                                <div className="tw-bg-red-700 tw-rounded-lg tw-py-2 tw-px-3 tw-mx-3 tw-mt-3 tw-text-white">
                                    {error}
                                </div>
                            )}

                        {!isLoading && reservations.length === 0 && searched
                            && (
                                <div className="tw-bg-red-700 tw-rounded-lg tw-py-2 tw-px-3 tw-mx-3 tw-mt-3 tw-text-white">
                                    No results found
                                </div>
                            )}

                        {/* Results */}
                        {reservations.map((mappedReservation) =>
                        {
                            const request = get(mappedReservation, 'request', {}) || {};
                            const hasFlights = mappedReservation.reservationFlights?.length > 0 ?? false;
                            const hasHotels = mappedReservation.reservationHotels?.length > 0 ?? false;
                            const hasInsurances = mappedReservation.reservationInsurances?.length > 0 ?? false;
                            const hasServices = mappedReservation.reservationServices?.length > 0 ?? false;

                            return (
                                <div
                                    key={mappedReservation.id}
                                    className="tw-flex tw-flex-col tw-w-full tw-justify-center tw-m-auto"
                                >

                                    {/* Result Compact */}
                                    <div className={!mappedReservation.showFull ? '' : 'tw-hidden'}>
                                        <button
                                            className="tw-display-block tw-w-full"
                                            type="button"
                                            onClick={() =>
                                            {
                                                toggleViewReservation(mappedReservation.id);
                                            }}
                                        >
                                            <div
                                                className="tw-text-gray-500 tw-rounded-lg tw-overflow-hidden tw-shadow-lg tw-bg-gray-100 tw-m-3 tw-block tw-cursor-pointer tw-opacity-75 hover:tw-opacity-100"
                                                role="button"
                                                tabIndex="0"
                                            >
                                                <div className="tw-py-4 tw-px-6">
                                                    <div className="tw-flex tw-flex-wrap sm:tw-justify-between tw-mt-1">
                                                        <h4 className="tw-text-gray-800 tw-w-full tw-text-center lg:tw-w-auto lg:tw-text-left tw-mb-2 lg:tw-mb-0 tw-uppercase font-md default-letter-spacing tw-font-bold tw-mt-1 tw-inline-block sm:tw-block">
                                                            {ellipsis(request.quote_and_invoice_name, 20)}
                                                        </h4>
                                                        <div className="tw-w-full lg:tw-w-auto tw-text-center lg:tw-text-right">
                                                            <span className="tw-mr-4 tw-inline-block tw-mb-2 lg:tw-mb-0">
                                                                {hasFlights && (
                                                                    <span className="material-icons rotate-90 tw-mx-2 tw-align-bottom">
                                                                        flight
                                                                    </span>
                                                                )}
                                                                {hasHotels && (
                                                                    <span className="material-icons tw-mx-2 tw-align-bottom">
                                                                        hotel
                                                                    </span>
                                                                )}
                                                                {hasServices && (
                                                                    <span className="material-icons tw-mx-2 tw-align-bottom">
                                                                        directions_bus
                                                                    </span>
                                                                )}
                                                                {hasInsurances && (
                                                                    <span className="material-icons tw-mx-2 tw-align-bottom">
                                                                        verified_user
                                                                    </span>
                                                                )}

                                                            </span>
                                                            <span className="tw-bg-gray-500 tw-text-gray-800 tw-rounded-full tw-py-1 tw-px-4 tw-inline-block tw-mr-4">
                                                                Booking No. {mappedReservation.booking_number}
                                                            </span>
                                                            <span className="material-icons tw-align-middle tw-hidden lg:tw-inline-block">
                                                                keyboard_arrow_down
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </button>
                                    </div>

                                    {/* Result Full */}
                                    <div className={mappedReservation.showFull ? '' : 'tw-hidden'}>
                                        <div className="tw-text-gray-500 tw-rounded-lg tw-overflow-hidden tw-shadow-lg tw-bg-gray-100 tw-m-3 tw-block">
                                            <div className="tw-py-8 tw-px-6">
                                                <div className="tw-flex tw-justify-end">
                                                    <div className="tw-w-auto tw-text-right">
                                                        <span
                                                            className="link-style"
                                                            onClick={() =>
                                                            {
                                                                toggleViewReservation(mappedReservation.id);
                                                            }}
                                                            role="button"
                                                            tabIndex="0"
                                                            onKeyUp={() =>
                                                            {
                                                                toggleViewReservation(mappedReservation.id);
                                                            }}
                                                        >
                                                            View less
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="width-fit-content tw-m-auto tw-mb-5">
                                                    <h4 className="tw-uppercase tw-my-2 font-md default-letter-spacing tw-font-bold tw-text-center">
                                                        “{request.quote_and_invoice_name}”
                                                    </h4>
                                                    <h4 className="tw-text-center">
                                                        <strong>
                                                            Booking No. {mappedReservation.booking_number}
                                                        </strong>
                                                    </h4>
                                                </div>

                                                {/* PDF's */}
                                                <div className="tw-flex tw-justify-center sm:tw-justify-end tw-mb-5">
                                                    {(!mappedReservation && request.secure_id) && (
                                                        <a
                                                            href={webQuoteUrl(request.secure_id, request.brand)}
                                                            className="tw-bg-blue-600 hover:tw-bg-blue-500 tw-text-white tw-py-1 tw-px-4 tw-rounded-full tw-mr-2"
                                                            target="_blank"
                                                        >
                                                            Quote
                                                        </a>
                                                    )}

                                                    {(mappedReservation && mappedReservation.secure_id) && (
                                                        <>
                                                            <a
                                                                href={webInvoiceUrl(mappedReservation.secure_id, request.brand)}
                                                                className="tw-bg-blue-600 hover:tw-bg-blue-500 tw-text-white tw-py-1 tw-px-4 tw-rounded-full tw-mr-2"
                                                                target="_blank"
                                                            >Invoice</a>
                                                            <a
                                                                href={webItineraryUrl(mappedReservation.secure_id, request.brand)}
                                                                className="tw-bg-blue-600 hover:tw-bg-blue-500 tw-text-white tw-py-1 tw-px-4 tw-rounded-full"
                                                                target="_blank"
                                                            >Itinerary</a>
                                                        </>
                                                    )}
                                                </div>

                                                <ReservationComponents
                                                    reservationId={mappedReservation.id}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </section>
            </div>
        </div>
    );
}

SearchReservation.defaultProps = {
    onClose: null,
};

SearchReservation.propTypes = {
    onClose: PropTypes.func,
    searchMemberReservations: PropTypes.func,
};

const mapDispatchToProps = (dispatch) => ({
    searchMemberReservations(query, includes)
    {
        return dispatch(loadReservations(query, includes));
    },
});

export default connect(null, mapDispatchToProps)(SearchReservation);
